import { IonHeader, IonItem, IonLabel, IonList, IonPage, IonToggle } from "@ionic/react";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/button";
import { Content } from "@/components/content";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { EditableAvatar } from "@/components/molecules/auth/editable-avatar";
import { OrganizationDropdown } from "@/components/molecules/organization/organization-dropdown";
import { Toolbar } from "@/components/toolbar";
import { env } from "@/env";
import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";
import { useOrganization } from "@/lib/backend/utils/hooks/use-organization";
import { useDarkModeAvailable } from "@/lib/hooks/use-appearance";
import { useMe } from "@/lib/hooks/use-me";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { chevronRightIcon } from "@/lib/icons/chevron-right.icon";
import { useUpdateAppVersion } from "@/lib/query/me/use-app-version";
import { useMyOrganizations } from "@/lib/query/organization/use-my-organizations";
import { Routes } from "@/lib/router";
import { cleanupDeviceRegistration } from "@/lib/utils/notifications";
import { authClient } from "@/utils/auth";
import packageJson from "../../../package.json";

export function SettingsPage() {
  const { t } = useTranslation();
  const PreferredHaptics = usePreferredHaptics();
  const modalSignOut = useRef<HTMLIonModalElement>(null);
  const modalDebugInfo = useRef<HTMLIonModalElement>(null);
  const { me, isNotEmployee } = useMe();
  const { data: organizationsData } = useMyOrganizations();
  const [versionClickCount, setVersionClickCount] = useState(0);
  const [debugInfo, setDebugInfo] = useState<Record<string, unknown>>({});

  const organizations = organizationsData?.organizations ?? [];
  const hasMultipleOrganizations = organizations.length > 1;
  const organizationSlug = import.meta.env.VITE_ORGANIZATION_SLUG || "boomer";
  const shouldShowMultipleOrganizations =
    hasMultipleOrganizations && (!organizationSlug || organizationSlug === "boomer");

  const activeOrganization = organizations.find(
    (org) => org.id === organizationsData?.activeOrganizationId,
  );

  // The personal way back, offered only where there is something to step out of: with
  // `appReworkEnabled` off this organization never got the herziening, so a switch here would
  // change nothing at all. A control that quietly does nothing is worse than no control.
  const { data: featureFlags } = useFeatureFlags();
  const updateAppVersion = useUpdateAppVersion();

  const darkModeAvailable = useDarkModeAvailable();

  // Fetch organization data to check if terms and conditions are set
  const { data: organization } = useOrganization();
  const termsAndConditions = organization?.termsAndConditions;

  // Check if terms is a URL or HTML content
  const isTermsUrl =
    termsAndConditions &&
    (termsAndConditions.startsWith("http://") || termsAndConditions.startsWith("https://"));
  const hasCustomTermsContent = termsAndConditions && !isTermsUrl;

  return (
    <IonPage>
      {/* Profiel en Berichten waren de enige twee tabbladen zonder kop: hun titel stond gewoon in
          de scroll en verdween dus zodra je las. Op iOS houdt het beginscherm van een tabblad zijn
          titel vast -- dat is hoe je weet waar je bent als je via een pushbericht binnenkomt. */}
      <IonHeader>
        <Toolbar className="ion-no-border plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold leading-[1.2] text-txt-primary">
              {t("routes:tabs.profile")}
            </h1>
            <OrganizationDropdown />
          </div>
        </Toolbar>
      </IonHeader>

      <Content scrollUnderChrome className="part-scroll:gap-6">
        <div className="flex flex-col items-center gap-3 py-6">
          <EditableAvatar userId={me?.id ?? ""} imageKey={me?.image ?? ""} className="shadow-lg" />

          <div className="flex flex-col items-center gap-1">
            <strong className="text-xl text-txt-primary">{me?.name}</strong>
            {/* #372 — toon de teams van de medewerker i.p.v. het generieke
                'Werknemer'-label (iedereen is werknemer). Niet-werknemers (zeldzaam)
                houden hun type-label; een werknemer zonder team toont niets. */}
            {isNotEmployee ? (
              <strong className="text-lg text-brand-500">{t("common:user.type.user")}</strong>
            ) : me?.employee?.teams && me.employee.teams.length > 0 ? (
              <span className="text-lg text-brand-500">
                {me.employee.teams.map((team) => team.name).join(" · ")}
              </span>
            ) : null}
          </div>
        </div>

        <IonList lines="none">
          <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.PersonalInformation()}>
            <IonLabel>{t("routes:personal-information.title")}</IonLabel>
          </IonItem>
          <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.Notifications()}>
            <IonLabel>{t("routes:notifications.title")}</IonLabel>
          </IonItem>
          <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.Calendar()}>
            <IonLabel>{t("routes:calendar.title")}</IonLabel>
          </IonItem>
          {/*
            Offered only where there is something to choose. With the beta off this organization has
            one appearance, and a row leading to a picker that cannot change anything is worse than
            no row — the same rule the app-version switch below follows.
          */}
          {darkModeAvailable && (
            <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.Appearance()}>
              <IonLabel>{t("routes:appearance.title")}</IonLabel>
              <span
                slot="end"
                className="rounded-full bg-surface-brand-primary px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-txt-brand"
              >
                {t("settings.appearance.beta.badge")}
              </span>
            </IonItem>
          )}
          <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.Language()}>
            <IonLabel>{t("common:language.name")}</IonLabel>
          </IonItem>
          <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.Privacy()}>
            <IonLabel>{t("routes:privacy.title")}</IonLabel>
          </IonItem>
          {hasCustomTermsContent ? (
            <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.Terms()}>
              <IonLabel>{t("routes:terms.title")}</IonLabel>
            </IonItem>
          ) : isTermsUrl ? (
            <IonItem detail detailIcon={chevronRightIcon} href={termsAndConditions}>
              <IonLabel>{t("routes:terms.title")}</IonLabel>
            </IonItem>
          ) : (
            <IonItem
              detail
              detailIcon={chevronRightIcon}
              href="https://admin.heyboomer.nl/terms-of-service"
            >
              <IonLabel>{t("routes:terms.title")}</IonLabel>
            </IonItem>
          )}
          {/* Alleen waar er iets te kiezen valt. In OFF en VERPLICHT staat er niets: een
              schakelaar die niets doet is erger dan geen schakelaar, want hij belooft een uitweg
              die er niet is. De server zegt of die er is -- de app rekent dat niet zelf na. */}
          {featureFlags.appReworkSwitchable && (
            <IonItem>
              <IonLabel className="whitespace-normal">
                <span className="text-txt-primary">{t("common:app-version.name")}</span>
                <p className="text-sm text-txt-secondary">{t("common:app-version.description")}</p>
              </IonLabel>
              <IonToggle
                slot="end"
                aria-label={t("common:app-version.name")}
                disabled={updateAppVersion.isPending}
                checked={featureFlags.appReworkEnabled}
                onIonChange={(event) => {
                  // De keuze gaat naar de server en niet meer naar de opslag van het toestel: daar
                  // was hij per apparaat verschillend, onzichtbaar voor het beheer en niet te
                  // overrulen.
                  //
                  // De trilling is fire-and-forget. Capacitor's web-haptics gooit waar
                  // `navigator.vibrate` niet bestaat -- Safari, desktop, een iOS-PWA -- en erop
                  // wachten liet de uitweg waar deze hele uitrol op rust stilletjes mislukken op
                  // precies de platforms waar hij nodig kon zijn.
                  updateAppVersion.mutate(event.detail.checked);
                  PreferredHaptics.impact();
                }}
              />
            </IonItem>
          )}
          <IonItem
            detail
            detailIcon={chevronRightIcon}
            button
            onClick={() => modalSignOut.current?.present()}
          >
            <IonLabel>{t("modals:sign-out.trigger")}</IonLabel>
          </IonItem>
          <IonItem
            className="text-xs text-txt-secondary"
            lines="none"
            onClick={async () => {
              await Haptics.impact({ style: ImpactStyle.Light });

              const newCount = versionClickCount + 1;
              setVersionClickCount(newCount);

              if (newCount >= 5) {
                const devInfo = {
                  version: packageJson.version,
                  mode: import.meta.env.MODE,
                  prod: import.meta.env.PROD,
                  dev: import.meta.env.DEV,
                  organizationSlug: import.meta.env.VITE_ORGANIZATION_SLUG,
                  backendUrl: import.meta.env.VITE_BACKEND_URL,
                  hostUrl: import.meta.env.VITE_HOST_URL,
                };

                await Haptics.impact({ style: ImpactStyle.Heavy });

                setDebugInfo(devInfo);
                modalDebugInfo.current?.present();
                setVersionClickCount(0);
              }
            }}
          >
            <IonLabel>Versie: {packageJson.version}</IonLabel>
          </IonItem>
          <ModalSheetAutoHeight
            ref={modalSignOut}
            onWillDismiss={async (event) => {
              if (event.detail.role === "confirm") {
                // Cleanup device registration before signing out
                await cleanupDeviceRegistration();
                await authClient.signOut();

                // IMPORTANT: Force full page reload instead of using router navigation
                // After sign-out, the nested IonRouterOutlet stack can become corrupted,
                // causing navigation issues (e.g., forgot password link not working).
                // A hard reload ensures the entire router state is properly reset.
                window.location.href = Routes.AuthSignInPassword();
              }
            }}
          >
            <div className="ion-padding flex flex-col gap-6 pt-6">
              <strong className="text-primary">{t("modals:sign-out.title")}</strong>

              <div className="flex flex-col gap-2">
                <Button onClick={() => modalSignOut.current?.dismiss(true, "confirm")}>
                  {t("modals:sign-out.confirm")}
                </Button>
                <Button
                  color="primary"
                  fill="outline"
                  onClick={() => modalSignOut.current?.dismiss()}
                >
                  {t("modals:sign-out.cancel")}
                </Button>
              </div>
            </div>
          </ModalSheetAutoHeight>

          <ModalSheetAutoHeight ref={modalDebugInfo}>
            <div className="ion-padding flex flex-col gap-6 pt-6">
              <strong className="text-primary">Development Info</strong>

              <div className="flex flex-col gap-2">
                {Object.entries(debugInfo).map(([key, value]) => (
                  <div key={key} className="flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase text-txt-secondary">
                      {key}
                    </span>
                    <span className="font-mono text-sm">{String(value)}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2)).then(() => {
                      toast.success("Copied to clipboard");
                    });
                  }}
                >
                  Copy to Clipboard
                </Button>
                <Button
                  color="primary"
                  fill="outline"
                  onClick={() => modalDebugInfo.current?.dismiss()}
                >
                  Close
                </Button>
              </div>
            </div>
          </ModalSheetAutoHeight>
        </IonList>
      </Content>
    </IonPage>
  );
}
